import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, Check, Info, AlertTriangle, Megaphone, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const typeIcons = {
  Alert: <AlertTriangle className="h-4 w-4 text-orange-500" />,
  Update: <Info className="h-4 w-4 text-blue-500" />,
  Promo: <Megaphone className="h-4 w-4 text-purple-500" />,
  Warning: <AlertTriangle className="h-4 w-4 text-red-500" />,
  Payment: <Check className="h-4 w-4 text-orange-500" />,
  System: <Bell className="h-4 w-4 text-slate-400" />,
  cart_reminder: <Megaphone className="h-4 w-4 text-orange-500" />,
};

const NotificationsDropdown = ({ isOpen, onClose, primaryColor }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      markAllAsRead();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const userId = user ? (user.u_id || user.user_id || user.id) : '';
      const res = await fetch(`/api/notifications/public${userId ? `?userId=${userId}` : ''}`);
      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    try {
      await fetch(`/api/notifications/read-all`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.u_id || user.user_id || user.id })
      });
      // Optionally notify Header or just wait for interval
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const dismissNotification = async (e, id) => {
    e.stopPropagation(); // Don't trigger redirect
    if (!user) return;
    try {
      await fetch(`/api/notifications/${id}/dismiss`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.u_id || user.user_id || user.id })
      });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const markAsRead = async (id) => {
    if (!user) return;
    const userId = user.u_id || user.user_id || user.id;
    try {
       await fetch(`/api/notifications/${id}/read`, {
           method: 'PATCH',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ userId })
       });
       setNotifications(prev => prev.map(n => 
           n.id === id ? { ...n, read_by: [...(n.read_by || []), userId] } : n
       ));
    } catch (error) {
        console.error('Error marking as read:', error);
    }
  };

  const isRead = (notification) => {
      if (!user) return false;
      const userId = user.u_id || user.user_id || user.id;
      return notification.read_by?.includes(userId);
  };

  const unreadCount = notifications.filter(n => !isRead(n)).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-[80] lg:hidden" onClick={onClose} />
          
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 sm:-right-4 lg:right-20 top-full mt-2 w-[min(92vw,24rem)] sm:w-[24rem] bg-white rounded-3xl shadow-2xl border border-slate-100 z-[100] overflow-hidden flex flex-col max-h-[min(70vh,500px)]"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
              <div>
                <h3 className="font-black text-slate-900 flex items-center gap-2">
                  Notifications
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] text-white font-black uppercase tracking-widest" style={{ backgroundColor: primaryColor }}>
                      {unreadCount} New
                    </span>
                  )}
                </h3>
              </div>
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-slate-200 transition-colors"
              >
                <X size={18} className="text-slate-400" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-10 flex flex-col items-center justify-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-slate-100 border-t-slate-400 animate-spin" />
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400">Syncing...</p>
                </div>
              ) : notifications.length > 0 ? (
                <div className="divide-y divide-slate-50">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      onClick={() => {
                        markAsRead(notification.id);
                        navigate(`/notifications/${notification.id}`);
                        onClose();
                      }}
                      className={`p-5 hover:bg-slate-50 transition-all cursor-pointer relative group ${!isRead(notification) ? 'bg-slate-50/30' : ''}`}
                    >
                      {!isRead(notification) && (
                        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: primaryColor }} />
                      )}
                      
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 mt-1">
                          {typeIcons[notification.type] || <Bell className="h-4 w-4 text-slate-400" />}
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`text-sm font-black text-slate-900 leading-tight ${!isRead(notification) ? '' : 'opacity-70'}`}>
                              {notification.title}
                            </h4>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black uppercase text-slate-400 whitespace-nowrap pt-0.5">
                                {new Date(notification.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                              </span>
                              <button 
                                onClick={(e) => dismissNotification(e, notification.id)}
                                className="p-1 rounded-full hover:bg-slate-200 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <X size={14} className="text-slate-400 hover:text-red-500" />
                              </button>
                            </div>
                          </div>
                          <p className={`text-xs font-medium text-slate-500 leading-relaxed line-clamp-2 ${!isRead(notification) ? '' : 'opacity-60'}`}>
                            {notification.message}
                          </p>
                          
                          {notification.image_url && (
                            <div className="mt-2 rounded-xl overflow-hidden border border-slate-100 bg-slate-50 aspect-video group-hover:shadow-lg transition-all">
                              <img src={notification.image_url} alt="Promo" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            </div>
                          )}

                          <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-400 pt-1">
                             <Clock size={10} />
                             {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 rounded-3xl bg-slate-50 flex items-center justify-center">
                    <BellOff size={32} className="text-slate-200" />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 uppercase tracking-tight">No notifications</h4>
                    <p className="text-xs font-medium text-slate-400 mt-1">We'll alert you when there's an update</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer removed */}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationsDropdown;
